import { getColors } from '@lib/theme'
import { PortableTextMarkComponentProps } from '@portabletext/react'
import { motion, stagger, Variants } from 'motion/react'
import {
  Children,
  cloneElement,
  isValidElement,
  ReactNode,
  useMemo,
} from 'react'
import { useIntersectionObserver } from 'usehooks-ts'

interface TypedTextValue {
  _type: 'textTyping'
  delay?: number
  showCusror?: boolean
}

const colorBlack = getColors().black
const colorTransparent = 'rgba(0, 0, 0, 0)'

/**
 * Wraps each character in spans for typewriter animation.
 */
const getWrappedCharacters = (
  children: ReactNode,
  characterCount: number,
  isIntersecting: boolean,
  delay: number,
  showCusror: boolean,
  currentCharacterIndex = 0,
): ReactNode => {
  const characterVariants: Variants = {
    hidden: {
      opacity: 0,
    },
    visible: {
      opacity: 1,
      transition: {
        ease: (value) => {
          return value > 0 ? 1 : 0
        },
      },
    },
  }
  const cursorVariants: Variants = {
    hidden: {
      backgroundColor: colorTransparent,
    },
    visible: {
      backgroundColor: [
        colorTransparent,
        colorBlack,
        colorBlack,
        colorTransparent,
      ],
      transition: {
        times: [0, 0, 1, 1],
        duration: delay / 1000,
      },
    },
  }
  const lastCursorVariants: Variants = {
    hidden: {
      backgroundColor: colorTransparent,
    },
    visible: {
      backgroundColor: [
        colorTransparent,
        colorBlack,
        colorBlack,
        colorTransparent,
        colorTransparent,
      ],
      transition: {
        times: [0, 0, 0.5, 0.5, 1],
        repeat: Infinity,
        duration: 1.06,
      },
    },
  }

  return Children.map(children, (child) => {
    if (typeof child === 'string' || typeof child === 'number') {
      return String(child)
        .split('')
        .map((character) => {
          currentCharacterIndex =
            (typeof currentCharacterIndex === 'undefined'
              ? 0
              : currentCharacterIndex) + 1
          const isLast = currentCharacterIndex === characterCount

          return (
            <motion.span key={currentCharacterIndex} className="relative">
              <motion.span variants={characterVariants}>
                {character}
              </motion.span>
              {showCusror && !isLast && (
                <motion.span
                  variants={cursorVariants}
                  className="absolute top-[5%] bottom-[5%] right-[-3px] w-[3px]"
                />
              )}
              {showCusror && isLast && (
                <motion.span
                  variants={lastCursorVariants}
                  className="absolute top-[5%] bottom-[5%] right-[-3px] w-[3px]"
                />
              )}
            </motion.span>
          )
        })
    }

    if (isValidElement(child)) {
      return cloneElement(child, {
        ...child.props,
        children: getWrappedCharacters(
          child.props.children,
          characterCount,
          isIntersecting,
          delay,
          showCusror,
          currentCharacterIndex,
        ),
      })
    }

    return child
  })
}

/**
 * Gets character count for each child.
 */
const getCharacterCount = (children: ReactNode): number => {
  const characterCounts =
    Children.map<number, ReactNode | string | number>(children, (child) => {
      if (typeof child === 'string' || typeof child === 'number') {
        return String(child).length
      }

      if (isValidElement(child)) {
        return getCharacterCount(child.props?.children)
      }

      return 0
    }) ?? []

  return characterCounts.reduce((total, count) => {
    return total + count
  }, 0)
}

const TypedText = ({
  value,
  children,
}: PortableTextMarkComponentProps<TypedTextValue>) => {
  const { ref, isIntersecting } = useIntersectionObserver({
    threshold: 1,
    freezeOnceVisible: true,
  })

  const delay = useMemo(() => {
    return value?.delay ?? 80
  }, [value])

  const showCusror = useMemo(() => {
    return Boolean(value?.showCusror)
  }, [value])

  const containerVariants = useMemo(() => {
    const containerVariants: Variants = {
      hidden: {},
      visible: {
        transition: {
          delayChildren: stagger(delay / 1000),
        },
      },
    }

    return containerVariants
  }, [delay])

  const characterCount = useMemo(() => {
    return getCharacterCount(children)
  }, [children])

  const wrappedChildren = useMemo(() => {
    const wrappedChildren = getWrappedCharacters(
      children,
      characterCount,
      isIntersecting,
      delay,
      showCusror,
    )

    return wrappedChildren
  }, [characterCount, children, delay, isIntersecting, showCusror])

  return (
    <motion.span
      ref={ref}
      variants={containerVariants}
      initial="hidden"
      animate={isIntersecting ? 'visible' : undefined}
    >
      {wrappedChildren}
    </motion.span>
  )
}

export default TypedText
